import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import './index.css';
// eslint-disable-next-line import/no-unresolved
import './css/tailwind.out.css';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import queryString from 'query-string';
import 'moment/locale/zh-tw';
import { createMuiTheme, ThemeProvider } from '@material-ui/core';
import './i18n';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Header from './webframe/Header';
import Footer from './webframe/Footer';
import ScrollToTop from './ScrollToTop';
import { LazyLoadingProvider } from './LazyLoading';
import Routes from './Routes';
import ProgressBackdrop from './preorder/components/ProgressBackdrop';
import { ProgressProvider, useProgressContext } from './preorder/contexts/useProgressContext';
import { withAuthProvider } from './preorder/plugins/Auth';
import withGlobalModalContextProvider from './hocs/withGlobalModalContextProvider';
import { ActivityProvider } from './activity/contexts/useActivityContext';
import { GlobalStoreProvider } from './contexts/globalStoreContext';
import Activity from './activity/index';
import defaultTheme from './defaultTheme';
import { SnackbarProvider } from './menu/contexts/menuToast';
import withECContextProviders from './ec/contexts';
import Readonly from './location';
import ECSearchReadonly from './ec/components/search';
import PartnerProvider from './pages/PartnerProvider';

const ProgressBackdropWithUseProgressContext = () => {
  const { isFinished } = useProgressContext();

  return <ProgressBackdrop isShow={!isFinished} />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      staleTime: Infinity,
      useErrorBoundary: false,
    },
  },
});

const Root = withECContextProviders(
  withGlobalModalContextProvider(
    withAuthProvider(() => {
      const theme = createMuiTheme(defaultTheme);

      useEffect(() => {
        const queryStringObj = queryString.parse(window.location.search);
        const utmInfo = {};
        Object.entries(queryStringObj)
          .filter(([key]) => key.includes('utm'))
          .forEach(([key, value]) => {
            utmInfo[key] = value;
          });
        if (Object.keys(utmInfo).length) localStorage.setItem('utmInfo', JSON.stringify(utmInfo));
        else localStorage.removeItem('utmInfo');
      }, []);

      return (
        <LazyLoadingProvider>
          <ThemeProvider theme={theme}>
            <GlobalStoreProvider>
              <ProgressProvider>
                <ActivityProvider>
                  <BrowserRouter>
                    <ProgressBackdropWithUseProgressContext />
                    <SnackbarProvider>
                      <QueryClientProvider client={queryClient}>
                        <Switch>
                          <Route path="/location-readonly" component={Readonly} />
                          <Route path="/partner_provider_arkbeez">
                            <PartnerProvider formLink="https://forms.gle/ZPVh87qem5AFoheXA" />
                          </Route>
                          <Route path="/ec-search-readonly" component={ECSearchReadonly} />
                          <Route path="/activity" component={Activity} />
                          <Route>
                            <Header />
                            <div className="fz-flex-grow fz-z-0">
                              <Switch>
                                {
                                Object.keys(Routes).map((routeName) => (
                                  <Route path={Routes[routeName].path} key={routeName}>
                                    {Routes[routeName].children}
                                  </Route>
                                ))
                              }
                              </Switch>
                              <ScrollToTop />
                            </div>
                            <Footer />
                          </Route>
                        </Switch>
                      </QueryClientProvider>
                    </SnackbarProvider>
                  </BrowserRouter>
                </ActivityProvider>
              </ProgressProvider>
            </GlobalStoreProvider>
          </ThemeProvider>
        </LazyLoadingProvider>
      );
    })
  )
);

ReactDOM.render(
  <Root />,
  document.getElementById('root')
);

// FOR ANALYTICS
const setupGA = () => {
  const headScript = document.createElement('script');
  headScript.text = `
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${process.env.REACT_APP_GTM_ID}');
  `;
  document.head.appendChild(headScript);
  const bodyScript = document.createElement('noscript');
  bodyScript.text = `
  <iframe src="https://www.googletagmanager.com/ns.html?id=${process.env.REACT_APP_GTM_ID}"
  height="0" width="0" style="display:none;visibility:hidden"></iframe>
  `;
  document.body.appendChild(bodyScript);
};
const setupFacebookPixel = () => {
  const headScript = document.createElement('script');
  headScript.text = `
      !function (f, b, e, v, n, t, s) {
          if (f.fbq) return; n = f.fbq = function () {
              n.callMethod ?
                  n.callMethod.apply(n, arguments) : n.queue.push(arguments)
          };
          if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
          n.queue = []; t = b.createElement(e); t.async = !0;
          t.src = v; s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s)
      }(window, document, 'script',
          'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '196175052096843');
      fbq('track', 'PageView');
      fbq('track', 'Freezone');
  `;
  document.head.appendChild(headScript);
  const headNoScript = document.createElement('noscript');
  headNoScript.text = `
  <img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=196175052096843&ev=PageView&noscript=1" />
  <img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=196175052096843&ev=Freezone&noscript=1" />`;
  document.head.appendChild(headNoScript);
};
const setupLineAd = () => {
  const headScript = document.createElement('script');
  headScript.text = `
    (function(g,d,o){
      g._ltq=g._ltq||[];g._lt=g._lt||function(){g._ltq.push(arguments)};
      var h=location.protocol==='https:'?'https://d.line-scdn.net':'http://d.line-cdn.net';
      var s=d.createElement('script');s.async=1;
      s.src=o||h+'/n/line_tag/public/release/v1/lt.js';
      var t=d.getElementsByTagName('script')[0];t.parentNode.insertBefore(s,t);
        })(window, document);
    _lt('init', {
      customerType: 'lap',
      tagId: '${process.env.REACT_APP_LINE_AD_TID}'
    });
    _lt('send', 'pv', ['${process.env.REACT_APP_LINE_AD_TID}']);
  `;
  document.head.appendChild(headScript);
  const headNoScript = document.createElement('noscript');
  headNoScript.text = `
    <img height="1" width="1" style="display:none"
        src="https://tr.line.me/tag.gif?c_t=lap&t_id=c55895b3-717b-408b-adfa-8138cb5e3407&e=pv&noscript=1" />
  `;
  document.head.appendChild(headNoScript);
};
try {
  setupGA();
  if (parseInt(process.env.REACT_APP_MODE, 10) === 1) {
    setupFacebookPixel();
  }
  setupLineAd();
} catch (error) {
  console.error(error); //eslint-disable-line
}
